/*
 * Transform call data into a dialer state description object.
 *
 * This is just a transformation, no local data are needed.
 *
 * Returns a list of currently ongoing calls from the PoV of
 * `exten` that is passed in as an argument. This is used by
 * the dialer to render the active call UI.
 *
 * Properties:
 * - call_id: Call linked ID
 * - status: connected / dialing / hold / disconnected / incomming
 * - peer: number
 * - queue: If the call is comming in from a queue, this is a queue name
 * - start_ts: Start timestamp of the call (whatever time needs to be
 *             shown in the dialer) Null, to hide the time.
 * - transfer:
 *   - status: dialing / connected
 *   - contact: number
 */
function project_call_to_exten(call, exten) {
	/*
	 * Returns [conn, my_chan, other_chan, role]
	 */
	function *find_connections_for_channel(chan_id) {
		for (let conn of call.connections) {
			if (conn.channel_caller && conn.channel_caller.channel == chan_id) {
				yield {conn, my_chan: conn.channel_caller, other_chan: conn.channel_callee, role: 'caller'};
			} else if (conn.channel_callee && conn.channel_callee.channel == chan_id) {
				yield {conn, my_chan: conn.channel_callee, other_chan: conn.channel_caller, role: 'callee'};
			}
		}
	}

	function find_my_channels() {
		const my_channels = new Set();

		for (const conn of call.connections)  {
			if (conn.channel_callee &&
				(((conn.path && conn.path.callee) || []).some(p => p.exten == exten) || conn.channel_callee.exten == exten)
			) {
				my_channels.add(conn.channel_callee.channel);
			}
			if (conn.channel_caller &&
				(((conn.path && conn.path.caller) || []).some(p => p.exten == exten) || conn.channel_caller.exten == exten)
			) {
				my_channels.add(conn.channel_caller.channel);
			}
		}
		
		// find exten in peers if not found in connections. There can be
		// orphan channels without connection for example calling to
		// queue after ringing all agents and before subsequent agents
		// ringing, or other internal calls from Asterisk to the extension
		if (my_channels.size <= 0) {
			for (const peer of call.peers) {
				if (peer.exten == exten) {
					my_channels.add(peer.channel);
				}
			}
		}
		
		// collect the channels from conn.peers
		return [...my_channels].map(channel => call.peers.find(p => p.channel == channel)).filter(i => i);
	}

	// console.log(call.peers.map(ch => ch.channel + ' = ' + ch.state + ", hold or musichold? " + (ch.hold_start_ts || ch.musichold)));
	
	/*
	 * When the call starts, it will have multiple connections
	 * between the original caller and all parallel ringing
	 * devices of the callee.
	 *
	 * These will be without connection.start_ts until the
	 * callee picks up the call on one of the devices.
	 *
	 * After that the former ringing connections will disappear.
	 */

	/*
	 * Find all channels with given exten and
	 * sort them by time they first appeared.
	 */
	let my_chans = find_my_channels().sort(function(a, b) {
		return a.first_time.localeCompare(b.first_time);
	});
	let my_connected_chans = my_chans.filter(c => c.state == 'connected');
	let my_other_chans = my_chans.filter(c => !my_connected_chans.includes(c));

	let status, peer, start_ts, early_ts, hold_start_ts, queue, transfer, my_chan;
	let debug = {
		dbc: call,
	};

	// if any channel has a queue set, we show it in the dialer
	let chanWithQueue = my_chans.find(c => c.queue);
	if (chanWithQueue) {
		queue = chanWithQueue.queue;
	} else {
		// if the queue was not found in channels, try connections. The
		// queue is set only for connection (amd not channels) for
		// outbound calls, where the queue is set with prefix and
		// stripped in dialplan
		let connWithQueue = call.connections.find(c => c.queue);
		if (connWithQueue) {
			queue = connWithQueue.queue;
		}
	}

	if (my_connected_chans.length == 0) {
		// I am not on hold or connected
		let dialing = my_other_chans.find(c => c.state == 'dialing' || c.originate_target);
		if (dialing) {
			// I am dialing someone
			status = 'dialing';
			peer = dialing.originate_target || dialing.connected_line;
			early_ts = dialing.first_time;
			my_chan = dialing;
		} else if (my_other_chans.length > 0) {
			// I am being dialed
			status = 'incomming';
			peer = my_other_chans[0].connected_line;
			early_ts = my_other_chans[0].first_time;
			my_chan = my_other_chans[0];
		} else {
			// My status is unknown, ignore this call
			return null;
		}
	} else {
		peer = my_connected_chans[0].connected_line;
		my_chan = my_connected_chans[0];

		if (my_connected_chans[0].hold_start_ts) {
			// I put this call on hold
			status = 'hold';
			hold_start_ts = my_connected_chans[0].hold_start_ts;
		} else if (my_connected_chans[0].musichold) {
			// I'm on hold in this call (by the action of remote
			// party)
			status = 'musichold';
		} else {
			// I'm connected in this call

			if (my_connected_chans[0].originate_caller) {
				// for originate calls set status = 'connected' only if the channel is truly connected
				// to another channel via bridges
				if (my_connected_chans[0].bridge_state === 'connected') {
					status = 'connected';
				} else {
					status = 'dialing';
				}
			} else {
				status = 'connected';
			}
		}

		// Find the channels my channel is connected to and the
		// connections. Make sure the list is sorted by
		// first appearance (dial_ts)
		let conns = [...find_connections_for_channel(my_connected_chans[0].channel)]
			.sort((c1, c2) => new Date(c1.conn.dial_ts).getTime() - new Date(c2.conn.dial_ts).getTime());
		if (conns[0]) {
			start_ts = conns[0].conn.start_ts;
		}
		
		// collect connection from/to my channel, but exclude the same other side
		//  - this is needed to exclude multiple connection to same exten (assuming the attended transfer is not done to the same extension)
		const other_conns = conns[0] ? conns.slice(1).filter(conn => conn.other_chan && conns[0].other_chan && conn.other_chan.caller !== conns[0].other_chan.caller) : [];
		
		// TODO:
		//  - fix peer for dialing ATX (should not be end peer)
		
		
		if (conns[0] && conns[0].other_chan && (other_conns.length > 0 || my_chan.atxfer) && my_chan.hold_start_ts) {
			// there is an ongoing attended transfer - detected either based on multiple connection
			// or based on atxfer flag set from deepBE in case of starting ATX, where the other connection
			// is not yet created

			peer = conns[0].other_chan.caller;

			if (other_conns.length > 0) {
				// there are more connections - we can detect the peer and transfer status

				// detect if there is a connected connection - this means the transfer is connected
				let connected_other_conn = other_conns.find(c => c.other_chan && c.other_chan.state == 'connected');
				if (connected_other_conn) {
					transfer = {
						status: 'connected',
						peer: connected_other_conn.my_chan.connected_line 
					};
				} else {
					transfer = {
						status: 'dialing',
						peer: other_conns[0].conn.queue || conns[0].my_chan.connected_line,
					};
				}
			} else {
				// there is only one connection, the attended transfer is just starting (assume my_chan.atxfer = true)
				transfer = {
					status: 'dialing',
					peer: null, // peer is not yet known
				};
			}
		}
	}

	const my_conn = call.connections.find(conn => (
		(conn.channel_caller && conn.channel_caller.channel == my_chan.channel) ||
		(conn.channel_callee && conn.channel_callee.channel == my_chan.channel)
	));

	// strip queue prefix from peer if set
	if (my_conn && my_conn.queue_prefix && peer.startsWith(my_conn.queue_prefix)) {
		peer = peer.substring(my_conn.queue_prefix.length);
	}

	return {
		call_id: call.call_id,
		status,
		peer,
		queue,
		early_ts,
		start_ts,
		hold_start_ts,
		transfer,
		chanspy_spied: my_conn && my_conn.chanspy_spied_exten,
		playing_recording: my_chan && my_chan.playing_recording,
		my_chans: my_chans.map(c => ({linked_id: c.linked_id, channel: c.channel})),
		debug,
	};
}
